import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { IconButton } from '@kainos/ui';
import { NotificationTray } from './NotificationTray';

/**
 * The topbar bell and the tray it opens.
 *
 * The badge counts unread notifications and nothing else. It used to light up
 * from the attention list, which the sidebar already counts — two controls
 * showing the same number is one of them lying about what it opens.
 */
export function NotificationBell({ unreadCount }: { unreadCount: number }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    // `pointerdown` rather than `click`: closing on mouse-up would swallow the
    // press that opened whatever the person clicked outside the tray.
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const label = unreadCount > 0 ? `Notificaciones (${unreadCount} sin leer)` : 'Notificaciones';

  return (
    <div ref={containerRef} className="relative">
      <IconButton
        ref={buttonRef}
        label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Bell size={16} strokeWidth={1.8} />
        {unreadCount > 0 ? (
          <span
            aria-hidden="true"
            className="absolute -right-1.5 -top-1.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full border-[1.5px] border-surface bg-warning px-[3px] text-[10px] font-bold leading-none text-white"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </IconButton>

      {open ? <NotificationTray onClose={() => setOpen(false)} /> : null}
    </div>
  );
}
